// JS mirror of common::routing::evaluate_scope from the C++ controller.
// Resolves an envelope's `to` field against the live connection set
// and returns the connection ids that should receive a copy. The
// in-memory broker uses this on top of the subscription fan-out so a
// publisher can target a specific session / role / node / participant.

import type { BrokerConnection, ConnectionState, To } from './types';

export interface ConnectionInfo {
  id: string;
  state: ConnectionState;
}

/**
 * Returns the set of connection ids the envelope should route to based
 * on its `to` scope. Returns an empty array when `to` is null or the
 * scope is `controller` (controller-bound envelopes do not fan out).
 */
export function evaluateScope(
  to: To | undefined,
  connections: Iterable<ConnectionState>,
): string[] {
  if (!to) return [];
  const out: string[] = [];
  switch (to.scope) {
    case 'controller':
      return [];
    case 'broadcast': {
      for (const c of connections) {
        if (c.registered) out.push(c.id);
      }
      return out;
    }
    case 'session': {
      if (!to.sessionId) return [];
      for (const c of connections) {
        if (!c.registered) continue;
        if (c.sessionId === to.sessionId) out.push(c.id);
      }
      return out;
    }
    case 'role': {
      if (!to.role) return [];
      for (const c of connections) {
        if (!c.registered) continue;
        if (c.role !== to.role) continue;
        if (to.sessionId && c.sessionId !== to.sessionId) continue;
        out.push(c.id);
      }
      return out;
    }
    case 'node': {
      if (!to.nodeId) return [];
      for (const c of connections) {
        if (!c.registered) continue;
        if (c.nodeId === to.nodeId) out.push(c.id);
      }
      return out;
    }
    case 'participant': {
      if (!to.participantId) return [];
      for (const c of connections) {
        if (!c.registered) continue;
        if (c.participantId === to.participantId) out.push(c.id);
      }
      return out;
    }
    default:
      return [];
  }
}

/** Helper for tests / sanity asserts. */
export function listConnections(map: Map<string, ConnectionState>): ConnectionInfo[] {
  const out: ConnectionInfo[] = [];
  for (const [id, state] of map) out.push({ id, state });
  return out;
}

export type { BrokerConnection };
